import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function I18nPage(): Node {
  return html`
    <div>
      <h1>Internationalization (i18n)</h1>
      <p><code>createI18n</code> provides reactive translations with interpolation. When the locale changes, all translated text in the UI updates automatically.</p>

      <h2>Setup</h2>
      ${CodeBlock(`import { createI18n } from 'onefold/i18n';

const { t, setLocale, locale } = createI18n({
  defaultLocale: 'en',
  translations: {
    en: {
      greeting: 'Hello, {{name}}!',
      items: '{{count}} items',
      nav: {
        home: 'Home',
        settings: 'Settings',
      },
    },
    es: {
      greeting: 'Hola, {{name}}!',
      items: '{{count}} elementos',
      nav: {
        home: 'Inicio',
        settings: 'Configuración',
      },
    },
  },
});`)}

      <h2>Using Translations</h2>
      ${CodeBlock(`function Header(): Node {
  return html\`
    <header>
      <h1>\${() => t('greeting', { name: 'World' })}</h1>
      <nav>
        <a href="/">\${() => t('nav.home')}</a>
        <a href="/settings">\${() => t('nav.settings')}</a>
      </nav>
      <p>Current locale: \${() => locale()}</p>
    </header>
  \`;
}`)}

      <h2>Switching Locale</h2>
      ${CodeBlock(`function LocaleSwitcher(): Node {
  return html\`
    <div>
      <button onclick=\${() => setLocale('en')}>English</button>
      <button onclick=\${() => setLocale('es')}>Español</button>
      <button onclick=\${() => setLocale('fr')}>Français</button>
    </div>
  \`;
}`)}

      ${Callout('Wrapping t() in an arrow function (e.g., () => t("key")) makes it reactive. The text updates when the locale signal changes.')}

      <h2>Interpolation</h2>
      <p>Use <code>{{placeholder}}</code> in translation strings:</p>
      ${CodeBlock(`// Translation: 'Hello, {{name}}! You have {{count}} messages.'
t('welcome', { name: 'Alice', count: 5 })
// → "Hello, Alice! You have 5 messages."`)}

      <h2>Nested Keys</h2>
      <p>Access nested translation objects with dot notation:</p>
      ${CodeBlock(`t('nav.home')      // → "Home"
t('nav.settings')  // → "Settings"`)}

      <h2>API</h2>
      ${raw(`<table>
        <tr><th>Export</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>t(key, params?)</code></td><td>(string, Record?) => string</td><td>Translate a key with optional interpolation.</td></tr>
        <tr><td><code>setLocale(locale)</code></td><td>(string) => void</td><td>Switch the active locale.</td></tr>
        <tr><td><code>locale()</code></td><td>Signal&lt;string&gt;</td><td>Current locale (reactive).</td></tr>
      </table>`)}

      ${Playground(`import { html, mount } from 'onefold';
import { createI18n } from 'onefold/i18n';

function App() {
  const i18n = createI18n({
    defaultLocale: 'en',
    fallbackLocale: 'en',
    messages: {
      en: {
        greeting: 'Hello, {name}!',
        welcome: 'Welcome to our application',
        items: '{count} item(s) in cart',
        'nav.home': 'Home', 'nav.about': 'About', 'nav.settings': 'Settings',
        'actions.save': 'Save', 'actions.cancel': 'Cancel', 'actions.delete': 'Delete',
      },
      es: {
        greeting: 'Hola, {name}!',
        welcome: 'Bienvenido a nuestra aplicacion',
        items: '{count} articulo(s) en el carrito',
        'nav.home': 'Inicio', 'nav.about': 'Acerca de', 'nav.settings': 'Configuracion',
        'actions.save': 'Guardar', 'actions.cancel': 'Cancelar', 'actions.delete': 'Eliminar',
      },
      fr: {
        greeting: 'Bonjour, {name}!',
        welcome: 'Bienvenue dans notre application',
        items: '{count} article(s) dans le panier',
        'nav.home': 'Accueil', 'nav.about': 'A propos', 'nav.settings': 'Parametres',
        'actions.save': 'Sauvegarder', 'actions.cancel': 'Annuler', 'actions.delete': 'Supprimer',
      },
    },
  });

  const locales = i18n.availableLocales();

  return html\`
    <div>
      <h3>\${() => i18n.t('greeting', { name: 'Developer' })}</h3>
      <p>\${() => i18n.t('welcome')}</p>
      <p style="font-size:13px;color:#666">\${() => i18n.t('items', { count: 3 })}</p>

      <div style="margin:16px 0;padding:12px;background:#f8fafc;border-radius:8px">
        <p style="font-size:13px;font-weight:600;margin-bottom:8px">Navigation:</p>
        <div style="display:flex;gap:12px">
          \${() => ['home', 'about', 'settings'].map(key => html\`
            <span style="padding:4px 10px;background:#e0e7ff;border-radius:4px;font-size:13px">
              \${i18n.t('nav.' + key)}
            </span>
          \`)}
        </div>
      </div>

      <div style="margin-bottom:16px">
        <p style="font-size:13px;font-weight:600;margin-bottom:8px">Actions:</p>
        <div style="display:flex;gap:8px">
          \${() => ['save', 'cancel', 'delete'].map(key => html\`
            <button>\${i18n.t('actions.' + key)}</button>
          \`)}
        </div>
      </div>

      <div style="border-top:1px solid #e5e7eb;padding-top:12px">
        <p style="font-size:13px;margin-bottom:8px">
          Active locale: <strong>\${() => i18n.locale()}</strong>
          | Available: \${locales.join(', ')}
        </p>
        <div style="display:flex;gap:8px">
          \${locales.map(loc => html\`
            <button
              onclick=\${() => i18n.setLocale(loc)}
              style=\${() => i18n.locale() === loc ? 'background:#4f46e5;color:white;border-color:#4f46e5' : ''}
            >\${loc.toUpperCase()}</button>
          \`)}
        </div>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createI18n — Reactive Multilingual App')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/theming">Theming</a> — reactive theme switching with CSS variables</li>
        <li><a href="/routing/router">Router</a> — client-side routing with nested routes</li>
      </ul>
    </div>
  `;
}
